// planning/SequencePlanner.ts
import type { StateSimulator } from './StateSimulator';
import type { PruningStrategy } from './PruningStrategy';
import type { PlanningResult, PlanningMetadata } from './PlanningResult';
import type { 
    PlanningState, 
    PlanningContext, 
    ActionSequence, 
    ActionCandidate,
    ActionComponent 
} from '../types/ai.types';
import { AIActionType } from '../types/ai.types';
import { PLANNING_CONFIG } from '../utils/AIConfig';

// ДОДАНО: Інтерфейс для нашого нового "Судді"
export interface TerminalEvaluator {
    evaluate(state: PlanningState, ctx: PlanningContext): number;
}

/**
 * Recursive multi-step action planner
 * Оновлено: Використовує архітектуру Terminal Node Evaluation
 */
export class SequencePlanner {
    private currentMetadata: PlanningMetadata = {
        candidatesGenerated: 0,
        topCandidates: [],
        prunedCount: 0,
        remainingCount: 0
    };
    
    constructor(
        private stateSimulator: StateSimulator,
        private pruningStrategy: PruningStrategy,
        private terminalEvaluator: TerminalEvaluator
    ) {}
    
    public planBestSequence(
        initialState: PlanningState,
        context: PlanningContext,
        components: ActionComponent[],
        maxDepth: number = 3
    ): PlanningResult {
        this.currentMetadata = {
            candidatesGenerated: 0,
            topCandidates: [],
            prunedCount: 0,
            remainingCount: 0
        };
        
        const sequence = this.planRecursive(
            initialState, 
            context, 
            components, 
            0, 
            maxDepth
        );

        if (PLANNING_CONFIG.debug) {
            // Тепер сортуємо ОДИН РАЗ в самому кінці по terminalScore
            this.currentMetadata.topCandidates.sort((a, b) => 
                (b.terminalScore ?? b.utility) - (a.terminalScore ?? a.utility)
            );
        }
        
        return {
            sequence: sequence && sequence.actions.length > 0 ? sequence : null,
            metadata: this.currentMetadata
        };
    }
    
    private planRecursive(
        state: PlanningState,
        ctx: PlanningContext,
        components: ActionComponent[],
        depth: number,
        maxDepth: number
    ): ActionSequence | null {
        // ═══════════════════════════════════════════════════════════
        // TERMINAL CONDITIONS (Тепер вони ОЦІНЮЮТЬ стан, а не просто відкидають)
        // ═══════════════════════════════════════════════════════════
        
        const lastAction = state.actionsUsed[state.actionsUsed.length - 1];
        
        // Якщо досягли ліміту глибини, стаміни, або юніт вирішив чекати — це кінець гілки.
        // Оцінюємо те, чого досягли на цей момент.
        if (depth >= maxDepth || state.stamina <= 0 || lastAction?.type === AIActionType.Wait) {
            return this.evaluateTerminalState(state, ctx);
        }
        
        // ═══════════════════════════════════════════════════════════
        // GENERATE CANDIDATES
        // ═══════════════════════════════════════════════════════════
        
        let allCandidates: ActionCandidate[] = [];
        
        for (const { generator, evaluator } of components) {
            // CONSTRAINT: No move after move
            if (lastAction?.type === AIActionType.Move && 
                generator.generatorType === AIActionType.Move) {
                continue;
            }
            
            // evaluator тут повертає ЕВРИСТИЧНУ утиліту (лише для прунінгу)
            const candidates = generator.generate(state, ctx, evaluator);
            allCandidates.push(...candidates);
        }
        
        // Якщо більше немає доступних дій, але ми ще не досягли макс. глибини
        // Це теж термінальний стан!
        if (allCandidates.length === 0) {
            return this.evaluateTerminalState(state, ctx);
        }
        
        // ═══════════════════════════════════════════════════════════
        // SORT & PRUNE
        // ═══════════════════════════════════════════════════════════
        
        allCandidates.sort((a, b) => b.utility - a.utility);
        
        if (depth === 0 && PLANNING_CONFIG.debug) {
            this.currentMetadata.candidatesGenerated = allCandidates.length;
            this.currentMetadata.topCandidates = allCandidates.slice(0, 10);
        }
        
        const prunedCandidates = this.pruningStrategy.prune(
            allCandidates,
            depth,
            ctx
        );
        
        if (depth === 0 && PLANNING_CONFIG.debug) {
            this.currentMetadata.prunedCount = allCandidates.length - prunedCandidates.length;
            this.currentMetadata.remainingCount = prunedCandidates.length;
        }
        
        // ═══════════════════════════════════════════════════════════
        // EXPLORE BRANCHES (Пошук найкращого ТЕРМІНАЛЬНОГО стану)
        // ═══════════════════════════════════════════════════════════
        
        let bestSequence: ActionSequence | null = null;
        let bestUtility = -999;
        
        for (const candidate of prunedCandidates) {
            
            // Симулюємо стан (StateSimulator має додати candidate.action до newState.actionsUsed)
            const newState = this.stateSimulator.simulate(state, candidate);
            
            // Рекурсивно йдемо далі. Зверни увагу: ми БІЛЬШЕ НЕ ДОДАЄМО candidate.utility.
            // Рекурсія сама дійде до кінця і поверне фінальну оцінку гілки.
            const branchSequence = this.planRecursive(
                newState,
                ctx,
                components,
                depth + 1,
                maxDepth
            );

            const totalUtility = branchSequence?.totalUtility ?? -999;

            // Просто зберігаємо результат прямо в об'єкт кандидата
            candidate.terminalScore = totalUtility; 

            // Якщо ця гілка привела до кращого фінального результату — запам'ятовуємо її
            if (totalUtility > bestUtility) {
                bestUtility = totalUtility;
                bestSequence = branchSequence;
                // bestSequence = {
                //     actions: [candidate.action, ...(branchSequence?.actions ?? [])],
                //     totalUtility,
                //     finalStamina: branchSequence?.finalStamina ?? newState.stamina
                // };
            }
            
        }
        
        // Fallback: якщо всі гілки якось обірвалися (не повинно статися), оцінюємо поточний стан
        return bestSequence || this.evaluateTerminalState(state, ctx);
    }

    /**
     * СЕРЦЕ НОВОЇ АРХІТЕКТУРИ
     * Перетворює пройдений стан на готову послідовність із правильною загальною утилітою.
     */
    private evaluateTerminalState(state: PlanningState, ctx: PlanningContext): ActionSequence {
        if (state.stamina < 0) return {
            actions: [...state.actionsUsed],
            totalUtility: -999,
            finalStamina: state.stamina
        }
        // Звертаємося до нашого "Судді", який знає про 0.3 (Move), 0.7 (Attack) та CV-дільники
        const finalUtility = this.terminalEvaluator.evaluate(state, ctx);

        return {
            actions: [...state.actionsUsed], // Весь ланцюжок дій
            totalUtility: finalUtility,
            finalStamina: state.stamina
        };
    }
}